import { datadogLogs } from '@datadog/browser-logs';
import { AppointmentStatus } from '@shared/types';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';

/**
 * Healthie-specific payload for beacon fallback.
 * Note: providerHealthieId is NOT included for security reasons.
 * The API route will look it up from session_note by roomId.
 */
export interface HealthieBeaconPayload {
  appointmentId: number;
  status: AppointmentStatus;
  duration: string;
  roomId: string;
}

/**
 * Pending session data for sendBeacon fallback.
 * Stored globally (outside React) to survive component unmounts.
 */
export interface PendingSessionData {
  roomId: string;
  duration: number;
  apptOccurredOnThePhone: boolean;
  status: AppointmentStatus;
  healthiePayload?: HealthieBeaconPayload;
}

/**
 * Active call data for forced beacon fallback.
 * When provider drops without opening End Session modal,
 * beforeunload saves duration to our DB only.
 * Healthie is updated later by the abandoned-call cron job.
 */
export interface ActiveCallData {
  roomId: string;
  startTime: number;
  healthieAppointmentId?: number;
}

// Global variables - survive React lifecycle
let pendingSessionData: PendingSessionData | null = null;
let activeCallData: ActiveCallData | null = null;

// Track if listener is already registered (prevents double registration)
let listenerRegistered = false;

/**
 * Set pending session data before starting end-call requests.
 * This data will be sent via sendBeacon if user closes tab before request completes.
 */
export function setPendingSessionData(data: PendingSessionData): void {
  pendingSessionData = data;

  // Note: This log may not be sent if tab is closed immediately after this call.
  // Datadog uses async batch sending which can be interrupted by tab closure.
  datadogLogs.logger.info(
    'Pending data set',
    callLogContext('session-beacon', {
      roomId: data.roomId,
      duration: data.duration,
    }),
  );
}

/**
 * Clear pending session data after successful request.
 */
export function clearPendingSessionData(): void {
  pendingSessionData = null;
}

/**
 * Clear only the Healthie payload after successful Healthie update.
 * Keeps session data for beacon fallback in case SOAP note generation fails.
 */
export function clearHealthiePayload(): void {
  if (pendingSessionData) {
    pendingSessionData = {
      ...pendingSessionData,
      healthiePayload: undefined,
    };
  }
}

/**
 * Get current pending session data (for debugging/testing).
 */
export function getPendingSessionData(): PendingSessionData | null {
  return pendingSessionData;
}

/**
 * Set active call data when both participants join.
 * Enables forced beacon if provider closes tab without End Session modal.
 */
export function setActiveCallData(data: ActiveCallData): void {
  activeCallData = data;

  datadogLogs.logger.info(
    'Active call data set',
    callLogContext('session-beacon', {
      roomId: data.roomId,
      startTime: data.startTime,
      healthieAppointmentId: data.healthieAppointmentId,
    }),
  );
}

/**
 * Clear active call data.
 * Called when provider confirms End Session (transitions to pendingSessionData)
 * or on component unmount.
 */
export function clearActiveCallData(): void {
  activeCallData = null;
}

/**
 * Get current active call data (for debugging/testing).
 */
export function getActiveCallData(): ActiveCallData | null {
  return activeCallData;
}

/**
 * Initialize the global beforeunload listener.
 * Called automatically when module is imported on client side.
 * Safe to call multiple times - will only register once.
 *
 * Note about logging: Datadog logs in the beforeunload handler may not be sent
 * if the tab closes too quickly. This is a known limitation of async logging.
 * The sendBeacon calls themselves are guaranteed to be sent by the browser.
 */
export function initSessionBeacon(): void {
  if (typeof window === 'undefined' || listenerRegistered) {
    return;
  }

  listenerRegistered = true;

  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    datadogLogs.logger.info(
      'beforeunload triggered',
      callLogContext('session-beacon', {
        roomId: pendingSessionData?.roomId ?? '',
        hasPendingData: !!pendingSessionData,
        hasHealthiePayload: !!pendingSessionData?.healthiePayload,
      }),
    );

    // Priority 1: Provider confirmed End Session modal — use exact data
    if (pendingSessionData) {
      const {
        roomId,
        duration,
        apptOccurredOnThePhone,
        status,
        healthiePayload,
      } = pendingSessionData;

      // Beacon 1: Save session details to local DB
      const sessionDetailsSuccess = navigator.sendBeacon(
        `/api/session-details/save/${roomId}`,
        JSON.stringify({
          duration,
          isEndCall: true,
          apptOccurredOnThePhone,
          status,
        }),
      );

      datadogLogs.logger.info(
        'Session details beacon sent',
        callLogContext('session-beacon', {
          roomId,
          duration,
          success: sessionDetailsSuccess,
        }),
      );

      // Beacon 2: Update Healthie status (if payload exists)
      if (healthiePayload) {
        const healthieSuccess = navigator.sendBeacon(
          '/api/healthie/appointment/status',
          JSON.stringify({
            ...healthiePayload,
            isBeaconRequest: true,
          }),
        );

        datadogLogs.logger.info(
          'Healthie status beacon sent',
          callLogContext('session-beacon', {
            roomId,
            appointmentId: healthiePayload.appointmentId,
            status: healthiePayload.status,
            success: healthieSuccess,
          }),
        );
      }

      // Show browser's native "Leave site?" dialog
      e.preventDefault();
      return;
    }

    // Priority 2: Provider in active call, didn't open End Session modal.
    // Only save duration to our DB — Healthie will be updated by cron after 2+ hours.
    // We intentionally do NOT update Healthie here because the provider may reconnect.
    if (activeCallData) {
      const { roomId, startTime, healthieAppointmentId } = activeCallData;
      const durationMs = Date.now() - startTime;
      const durationMinutes = Math.max(0, Math.round(durationMs / 60_000));

      if (durationMinutes > 0) {
        const success = navigator.sendBeacon(
          `/api/session-notes/duration/${roomId}`,
          JSON.stringify({ duration: durationMinutes }),
        );

        datadogLogs.logger.info(
          'Forced beacon: duration saved to DB',
          callLogContext('session-beacon', {
            roomId,
            healthieAppointmentId,
            duration: durationMinutes,
            success,
          }),
        );
      }
    }
  });

  datadogLogs.logger.info('Global beforeunload listener registered', {
    feature: 'video-call',
    workflow: 'session-beacon',
  });
}

// Auto-initialize on client side
if (typeof window !== 'undefined') {
  initSessionBeacon();
}
